import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { myAnchorHistory, myPulses, type AnchorHistoryRow } from '@/lib/pulse/api';

type Preset = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

const SERIES_COLORS = [
  'hsl(25, 88%, 51%)',
  'hsl(199, 89%, 48%)',
  'hsl(142, 71%, 45%)',
  'hsl(262, 83%, 58%)',
  'hsl(43, 96%, 56%)',
  'hsl(0, 84%, 60%)',
  'hsl(173, 80%, 40%)',
  'hsl(316, 73%, 52%)',
];

const PX_PER_POINT = 18;
const SCROLL_THRESHOLD_DAYS = 30;

function daysAgo(n: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d;
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <div className="font-medium text-foreground mb-1">{label}</div>
      <ul className="space-y-0.5">
        {payload.map((p: any) => (
          <li key={p.dataKey} className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="ml-auto tabular-nums text-foreground">
              {Number(p.value).toFixed(1)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MyTimelineChart({ orgId }: { orgId: string }) {
  const [preset, setPreset] = useState<Preset>('90d');
  const [custom, setCustom] = useState<{ from?: Date; to?: Date }>({});
  const [surveyId, setSurveyId] = useState<string>('all');
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const range = useMemo(() => {
    if (preset === 'custom' && custom.from && custom.to) {
      return { from: custom.from, to: custom.to };
    }
    const days = preset === '7d' ? 7 : preset === '30d' ? 30 : preset === '90d' ? 90 : preset === '1y' ? 365 : 3650;
    return { from: daysAgo(days), to: new Date() };
  }, [preset, custom]);

  const anchors = useQuery({
    queryKey: ['my_anchor_history', orgId, surveyId],
    queryFn: async () => {
      const { data, error } = await myAnchorHistory(orgId, surveyId === 'all' ? null : surveyId);
      if (error) throw error;
      return data as AnchorHistoryRow[];
    },
  });

  const pulses = useQuery({
    queryKey: ['my_pulses_full', orgId],
    queryFn: async () => {
      const { data, error } = await myPulses(orgId, 365);
      if (error) throw error;
      return (data ?? []) as Array<{ created_at: string; value: number }>;
    },
  });

  // surveys for filter dropdown
  const surveys = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of anchors.data ?? []) seen.set(r.survey_id, r.survey_name);
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [anchors.data]);

  // anchor series (one line per anchor)
  const anchorSeries = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of anchors.data ?? []) seen.set(r.anchor_id, r.anchor_name);
    return Array.from(seen.entries()).map(([id, name], i) => ({
      id, name, color: SERIES_COLORS[i % SERIES_COLORS.length],
    }));
  }, [anchors.data]);

  // Merge into per-day rows
  const chartData = useMemo(() => {
    const fromMs = range.from.getTime();
    const toMs = range.to.getTime();
    const byDay = new Map<string, Record<string, any>>();

    const ensure = (day: string) => {
      if (!byDay.has(day)) byDay.set(day, { day });
      return byDay.get(day)!;
    };

    for (const r of anchors.data ?? []) {
      const t = new Date(r.recorded_at).getTime();
      if (t < fromMs || t > toMs) continue;
      const day = r.recorded_at.slice(0, 10);
      const row = ensure(day);
      // latest value per anchor per day wins (rare to have multi/day)
      row[r.anchor_id] = r.value;
    }
    for (const p of pulses.data ?? []) {
      const t = new Date(p.created_at).getTime();
      if (t < fromMs || t > toMs) continue;
      const day = p.created_at.slice(0, 10);
      const row = ensure(day);
      // average if multiple pulses on the same day
      const prev = row.__pulse_sum ?? 0;
      const cnt = row.__pulse_cnt ?? 0;
      row.__pulse_sum = prev + p.value;
      row.__pulse_cnt = cnt + 1;
      row.pulse = (prev + p.value) / (cnt + 1);
    }

    return Array.from(byDay.values())
      .sort((a, b) => String(a.day).localeCompare(String(b.day)))
      .map((d) => ({ ...d, dayLabel: format(new Date(d.day + 'T00:00:00'), 'MMM d') }));
  }, [anchors.data, pulses.data, range]);

  const toggleSeries = (key: string) =>
    setHiddenSeries((s) => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  const isLoading = anchors.isLoading || pulses.isLoading;
  const err = (anchors.error || pulses.error) as Error | null;
  const scroll = chartData.length > SCROLL_THRESHOLD_DAYS;
  const minWidth = scroll ? chartData.length * PX_PER_POINT + 120 : undefined;

  const customLabel =
    preset === 'custom' && custom.from && custom.to
      ? `${format(custom.from, 'MMM d')} – ${format(custom.to, 'MMM d, yyyy')}`
      : 'Custom range';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={surveyId} onValueChange={setSurveyId}>
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue placeholder="Survey" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All surveys</SelectItem>
            {surveys.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ToggleGroup
          type="single"
          value={preset === 'custom' ? '' : preset}
          onValueChange={(v) => v && setPreset(v as Preset)}
          size="sm"
          variant="outline"
        >
          <ToggleGroupItem value="7d">7d</ToggleGroupItem>
          <ToggleGroupItem value="30d">30d</ToggleGroupItem>
          <ToggleGroupItem value="90d">90d</ToggleGroupItem>
          <ToggleGroupItem value="1y">1y</ToggleGroupItem>
          <ToggleGroupItem value="all">All</ToggleGroupItem>
        </ToggleGroup>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={preset === 'custom' ? 'default' : 'outline'}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              {customLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={{ from: custom.from, to: custom.to }}
              onSelect={(r: any) => {
                setCustom({ from: r?.from, to: r?.to });
                if (r?.from && r?.to) setPreset('custom');
              }}
              numberOfMonths={2}
              initialFocus
              className={cn('p-3 pointer-events-auto')}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Series toggles */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => toggleSeries('pulse')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition',
            hiddenSeries.has('pulse')
              ? 'border-border bg-muted/30 text-muted-foreground line-through'
              : 'border-foreground/20 bg-background text-foreground',
          )}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: 'hsl(var(--muted-foreground))' }} />
          Pulse
        </button>
        {anchorSeries.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => toggleSeries(s.id)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition',
              hiddenSeries.has(s.id)
                ? 'border-border bg-muted/30 text-muted-foreground line-through'
                : 'border-foreground/20 bg-background text-foreground',
            )}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
            {s.name}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="h-72 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : err ? (
        <p className="text-sm text-destructive">{err.message}</p>
      ) : chartData.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No pulse or anchor history in this range yet.
        </p>
      ) : (
        <div className={cn('w-full', scroll && 'overflow-x-auto')} style={{ height: 360 }}>
          <div style={{ minWidth, height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  yAxisId="pulse"
                  domain={[1, 10]}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={32}
                  label={{ value: 'Pulse 1–10', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }}
                />
                <YAxis
                  yAxisId="anchor"
                  orientation="right"
                  domain={[1, 5]}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={32}
                  label={{ value: 'Anchors 1–5', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {!hiddenSeries.has('pulse') && (
                  <Scatter
                    yAxisId="pulse"
                    dataKey="pulse"
                    name="Pulse"
                    fill="hsl(var(--muted-foreground))"
                    shape="circle"
                  />
                )}
                {anchorSeries.map((s) =>
                  hiddenSeries.has(s.id) ? null : (
                    <Line
                      key={s.id}
                      yAxisId="anchor"
                      type="monotone"
                      dataKey={s.id}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 0, fill: s.color }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ),
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Badge variant="secondary" className="text-[10px]">Private</Badge>
        Only you can see this timeline.
      </p>
    </div>
  );
}
